import { AsyncAction } from "@/Common/Api/AsyncActions/AsyncAction";
import { Client } from "@/Common/Api/Client";
import { SaveSendMessageRequest } from "@/Common/Api/Communications/Dto/SaveSendMessageRequest";
import { SaveSendMessageResponse } from "@/Common/Api/Communications/Dto/SaveSendMessageResponse";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { AcceptPopulationBasedFundingEventsResponse } from "@/Common/Api/PopulationBasedFunding/Dto/AcceptPopulationBasedFundingEventsResponse";
import { OverrideProposalEventResponse } from "@/Common/Api/PopulationBasedFunding/Dto/OverrideProposalEventsResponse";
import PatientHistoryResponse from "@/Common/Api/PopulationBasedFunding/Dto/PatientHistoryResponse";
import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { PopulationBasedFundingEventWithNotesModel } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingEventWithNotesModel";
import { PopulationBasedFundingFormData } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { MessageOrTaskContext, PopulationBasedFundingPortfolioSortOrder, PopulationBasedFundingStatus, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Server } from "@/Common/Operations/operations.server";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { CreatePopulationBasedFundingEventTaskResponse } from "@/Portfolio/Api/PopulationBasedFunding/Dto/CreatePopulationBasedFundingEventTaskResponse";
import { GetPopulationBasedFundingEventTasksResponse } from "@/Portfolio/Api/PopulationBasedFunding/Dto/GetPopulationBasedFundingEventTasksResponse";
import { GetProposalCountResponse } from "@/Portfolio/Api/PopulationBasedFunding/Dto/GetProposalCountResponse";
import { LoadPopulationBasedFundingPortfolioEventsResponse } from "@/Portfolio/Api/PopulationBasedFunding/Dto/LoadPopulationBasedFundingPortfolioEventsResponse";
import { PrintPopulationBasedFundingPatientRegistryResponse } from "@/Portfolio/Api/PopulationBasedFunding/Dto/PrintPopulationBasedFundingPatientRegistryResponse";
import { PopulationBasedFundingEventTask } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingEventTask";
import { PopulationBasedFundingPortfolioEvent } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingPortfolioEvent";
import { PopulationBasedFundingPortfolioModel } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingPortfolioModel";
import { PopulationBasedFundingPortfolioFilterSettings } from "@/Portfolio/Models/Settings/PopulationBasedFundingTableFilterSettings";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class Actions extends AsyncAction {
    private _portfolioStore: PortfolioStore;

    private get portfolioStore(): PortfolioStore {
        if (!this._portfolioStore)
            this._portfolioStore = portfolioStore;

        return this._portfolioStore;
    }

    private get model(): PopulationBasedFundingPortfolioModel {
        return this.portfolioStore.populationBasedFundingPortfolioModel;
    }

    constructor(portfolioStore: PortfolioStore) {
        super();
        this._portfolioStore = portfolioStore;
    }

    public async printPatientRegistry(
        filterSettings?: PopulationBasedFundingPortfolioFilterSettings,
        queryOrderType?: PopulationBasedFundingPortfolioSortOrder,
        queryOrderDirection?: SortDirection
    ): Promise<PrintPopulationBasedFundingPatientRegistryResponse> {
        const requestFilter = { ...(filterSettings ?? this.model.FilterSettings) };
        const requestData = {
            filterSettings: requestFilter,
            queryOrderType: queryOrderType ?? this.model.QueryOrderType,
            queryOrderDirection: queryOrderDirection ?? this.model.QueryOrderDirection
        };
        const response = await this.postAsync<PrintPopulationBasedFundingPatientRegistryResponse>(
            Server.Myle_Portfolio_PopulationBasedFunding_PrintPatientRegistry,
            requestData
        );
        return response;
    }

    public async loadPopulationBasedFundingPortfolioEvents(
        filterSettings?: PopulationBasedFundingPortfolioFilterSettings,
        queryOrderType?: PopulationBasedFundingPortfolioSortOrder,
        queryOrderDirection?: SortDirection,
        onSuccess?: () => void
    ): Promise<PopulationBasedFundingPortfolioEvent[]> {
        const requestFilter = { ...(filterSettings ?? this.model.FilterSettings) };

        const requestData = {
            filterSettings: requestFilter,
            queryOrderType: queryOrderType ?? this.model.QueryOrderType,
            queryOrderDirection: queryOrderDirection ?? this.model.QueryOrderDirection
        };

        const response = await this.postAsync<LoadPopulationBasedFundingPortfolioEventsResponse>(
            Server.Myle_Portfolio_GetFilteredPopulationBasedFundingEvents,
            requestData
        );

        if (!response.Errors.any() && onSuccess) {
            onSuccess();
        }

        this.dispatchLocal({
            Data: response.Events,
            SelectedEventIds: [],
            QueryOrderType: requestData.queryOrderType,
            QueryOrderDirection: requestData.queryOrderDirection,
            FilterSettings: {
                ...(filterSettings ?? this.model.FilterSettings),
                QueryCurrentPageNumber: response.QueryCurrentPageNumber,
                QueryRowCount: response.QueryRowCount
            }
        });

        return response.Events;
    }

    public toggleEventIsSelected(eventId: Guid | string): void {
        const nextSelection = this.isEventSelected(eventId)
            ? this.model.SelectedEventIds.filter(id => id !== eventId)
            : [...this.model.SelectedEventIds, eventId];

        this.dispatchLocal({ SelectedEventIds: nextSelection });
    }

    public isEventSelected(eventId: Guid | string): boolean {
        return this.model.SelectedEventIds.any(id => id === eventId);
    }

    public toggleSelectAllEvents(): void {
        const nextSelection = !this.getAreAllEventsSelected()
            ? this.model.Data.filter(a => this.eventIsAProposal(a)).map(event => event.Id)
            : [];
        this.dispatchLocal({ SelectedEventIds: nextSelection });
    }

    public getAreAllEventsSelected(): boolean {
        return this.model.Data.filter(a => this.eventIsAProposal(a)).length === this.model.SelectedEventIds.length;
    }

    public sortEventsList(sortingColumn: PopulationBasedFundingPortfolioSortOrder): void {
        const nextDirection = sortingColumn === this.model.QueryOrderType
            ? this.model.QueryOrderDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
            : SortDirection.ASC;

        this.loadPopulationBasedFundingPortfolioEvents(this.model.FilterSettings, sortingColumn, nextDirection);
    }

    public changePage(nextPageNumber: number): void {
        const nextFilterSettings: PopulationBasedFundingPortfolioFilterSettings = {
            ...this.model.FilterSettings,
            QueryCurrentPageNumber: nextPageNumber
        };

        this.loadPopulationBasedFundingPortfolioEvents(nextFilterSettings);
    }

    public updateSearchText(text: string): void {
        const nextFilterSettings: PopulationBasedFundingPortfolioFilterSettings = {
            ...this.model.FilterSettings,
            SearchText: text
        };

        this.loadPopulationBasedFundingPortfolioEvents(nextFilterSettings);
    }

    public acceptProposal(populationBasedFundingEventId: Guid | string): void {
        this.massAcceptProposal([populationBasedFundingEventId]);
    }

    public async massAcceptProposal(eventIds: (Guid | string)[]): Promise<void> {
        this.dispatchLocal({ IsMassAcceptDisabled: true });

        const response = await this.postAsync<AcceptPopulationBasedFundingEventsResponse>(
            Server.Myle_PopulationBasedFunding_AcceptProposals,
            {
                eventIds: eventIds
            }
        );

        if (!response.IsSuccess) return;

        this.dispatchLocal({ SelectedEventIds: [], IsMassAcceptDisabled: false });
        this.loadPopulationBasedFundingPortfolioEvents();
    }

    public openOverrideProposalPopup(eventModel: PopulationBasedFundingPortfolioEvent): void {
        this.dispatchLocal({
            EventToOverride: eventModel.toPopulationBasedFundingEventWithNotesModel()
        });
    }

    public closeOverrideProposalPopup(): void {
        this.dispatchLocal({
            EventToOverride: null
        });
    }

    public async overrideRegistrationProposalAndLoadHistory(selectedReasonId: number, customReason: string, note: string, eventId: string): Promise<OverrideProposalEventResponse> {
        const response = await this.postAsync<OverrideProposalEventResponse>(
            Server.Myle_PopulationBasedFunding_OverrideRegistrationProposal,
            {
                OverrideReason: selectedReasonId,
                CustomReason: customReason,
                Note: note,
                EventId: eventId
            }
        );

        if (response?.IsSuccess)
            PopulationBasedFundingPortfolioActions.loadPopulationBasedFundingPortfolioEvents();

        return response;
    }

    public async overrideDeregistrationProposalAndLoadHistory(selectedReasonId: number, customReason: string, note: string, eventId: string): Promise<OverrideProposalEventResponse> {
        const response = await this.postAsync<OverrideProposalEventResponse>(
            Server.Myle_PopulationBasedFunding_OverrideDeregistrationProposal,
            {
                OverrideReason: selectedReasonId,
                CustomReason: customReason,
                Note: note,
                EventId: eventId
            }
        );

        if (response?.IsSuccess)
            PopulationBasedFundingPortfolioActions.loadPopulationBasedFundingPortfolioEvents();

        return response;
    }

    public async createTask(patientContactFacetId: string, eventId: string): Promise<void> {
        const patient = await this.loadPatientModel(patientContactFacetId);

        sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
            isTask: true,
            patient: Person.FromPatient(patient),
            target: MessageOrTaskContext.PopulationBasedFunding,
            onSave: (requestData: SaveSendMessageRequest) => this.onSaveTask(requestData, eventId)
        });
    }

    public async onSaveTask(saveSendMessageRequestData: SaveSendMessageRequest, eventId: string): Promise<SaveSendMessageResponse> {
        const requestData = {
            EventId: eventId,
            ActionType: saveSendMessageRequestData.ActionType,
            Attachments: saveSendMessageRequestData.Attachments,
            Context: saveSendMessageRequestData.Context,
            Message: saveSendMessageRequestData.Message,
            PatientAttachments: saveSendMessageRequestData.PatientAttachments,
            PatientIds: saveSendMessageRequestData.PatientIds,
            PracticiansIds: saveSendMessageRequestData.PracticiansIds
        };

        const response = await this.postAsync<CreatePopulationBasedFundingEventTaskResponse>(
            Server.Myle_PopulationBasedFunding_CreateTask,
            requestData
        );

        this.loadTasksForPatientContactId(this.model.Data.map(event => event.PatientContactId));

        return response;
    }

    public async redirectToClickedAttachedTaskDetails(patientContactFacetId: string, individualMessageId: string): Promise<void> {
        const patient = await this.loadPatientModel(patientContactFacetId);

        sessionStore.dispatch(
            LocalOperations.Misc.RedirectToTab,
            {
                value: patient.IndividualResponse.Contact_id,
                currentLocation: RouterHistory.location,
                taskId: individualMessageId,
                target: RedirectTo.Patient
            }
        );
    }

    public openTasksListPopup(event: PopulationBasedFundingPortfolioEvent, mouseEvent: React.MouseEvent): void {
        this.dispatchLocal({
            ClickedEventTasksCounter: event,
            ClickedTasksCounterPosition: { x: mouseEvent.clientX, y: mouseEvent.clientY }
        });
    }

    public closeTasksListPopup(): void {
        this.dispatchLocal({
            ClickedEventTasksCounter: null
        });
    }

    public async openRegisterNewPatient(patientContactFacetId: string, eventId: string): Promise<void> {
        const patient = await this.loadPatientModel(patientContactFacetId);

        this.dispatchLocal({
            NewPatientToRegister: patient,
            InternalProposalToCompleteEventId: eventId
        });
    }

    public closeRegisterNewPatientPopup(): void {
        this.dispatchLocal({
            NewPatientToRegister: null
        });
    }

    public saveFilterSettings(
        filterSettings: PopulationBasedFundingPortfolioFilterSettings,
        isSaveSettingsChecked: boolean,
        onSuccess: () => void
    ): void {
        if (isSaveSettingsChecked) {
            this.saveUserSettings(filterSettings);
        }
        this.loadPopulationBasedFundingPortfolioEvents(filterSettings, null, null, onSuccess);
    }

    public async loadPatientRegistrationHistory(patientResponseContactFacetId: string): Promise<PopulationBasedFundingEventWithNotesModel[]> {
        const requestData = {
            PatientContactFacetId: patientResponseContactFacetId
        };

        const response = await this.postAsync<PatientHistoryResponse>(
            Server.Myle_PopulationBasedFundingEvent_GetPatientHistory,
            requestData
        );

        if (!response.IsSuccess)
            return;

        return response.PopulationBasedFundingEvents;
    }

    private async saveUserSettings(filterSettings: PopulationBasedFundingPortfolioFilterSettings): Promise<void> {
        const userSettings = new UserSettings();
        userSettings.PortfolioPopulationBasedFundingFilter = filterSettings;

        const requestData = {
            UserSettings: userSettings,
            UserId: sessionStore.userId
        };

        const response = await this.postAsync<GenericResponse>(
            Server.Myle_UpdateUserSettings,
            requestData
        );

        if (response?.IsSuccess) {
            cacheStore.UserSettings.PortfolioPopulationBasedFundingFilter = userSettings.PortfolioPopulationBasedFundingFilter;
        }
    }

    public async getActionableProposalCount(): Promise<number> {
        let response = await this.postAsync<GetProposalCountResponse>(Server.Myle_PopulationBasedFundingEvent_GetActionableProposalCount, null);

        if (!response?.IsSuccess) return;
        return response.ProposalCount;
    }

    public async deleteEvent(eventId: string): Promise<GenericResponse> {
        return this.postAsync<GenericResponse>(
            Server.Myle_PopulationBasedFundingEvent_Delete,
            { eventId: eventId }
        );
    }

    public async loadTasksForPatientContactId(patientContactIds: string[]): Promise<void> {
        if (!patientContactIds?.any() ?? true)
            return;

        const response = await this.postAsync<GetPopulationBasedFundingEventTasksResponse>(
            Server.Myle_PopulationBasedFunding_GetPatientsTasks,
            { PatientContactIds: patientContactIds }
        );

        if (!response.TasksDictionnary) return;
        const TasksByEventIdDictionnary: { [eventId: string]: PopulationBasedFundingEventTask[] } = {};

        Object.keys(response?.TasksDictionnary)
            .map(key => TasksByEventIdDictionnary[key] = response.TasksDictionnary[key].map(p => new PopulationBasedFundingEventTask(p)));

        this.dispatchLocal({ TasksByEventIdDictionnary });
    }

    public async acceptInternalRegistrationProposal(eventId: string, formData: PopulationBasedFundingFormData): Promise<GenericResponse> {
        return await this.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_PopulationBasedFunding_AcceptInternalRegistrationProposal,
            {
                EventId: eventId.toString(),
                EmployeeContactFacetId: formData.SelectedPhysician.ContactFacetId,
                EffectiveDate: formData.SelectedEffectiveDate,
                Notes: formData.Notes,
                AdministrativeCode: formData.AdministrativeCode
            }
        );
    }

    public async transferPatientRegister(
        sourcePhysicianContactFacetId: string,
        targetPhysicianContactFacetId: string
    ): Promise<boolean> {
        const response = await this.postAsync<GenericResponse>(
            ServerOperations.Server.Myle_PopulationBasedFunding_TransferPatientRegister,
            {
                SourcePhysicianContactFacetId: sourcePhysicianContactFacetId,
                TargetPhysicianContactFacetId: targetPhysicianContactFacetId
            }
        );

        return response?.IsSuccess ?? false;
    }

    private dispatchLocal(stateChanges: Partial<PopulationBasedFundingPortfolioModel>): void {
        sessionStore.dispatch(LocalOperations.Portfolio.PopulationBasedFunding_LoadEvents, { stateChanges });
    }

    private eventIsAProposal(event: PopulationBasedFundingPortfolioEvent): boolean {
        return event.Status === PopulationBasedFundingStatus.ProposedRegistration
        || event.Status === PopulationBasedFundingStatus.ProposedDeregistration;
    }

    private async loadPatientModel(patientContactFacetId: string): Promise<Patient> {
        let patient = patientStore.PatientModels[patientContactFacetId];

        if (!patient) {
            let response = await Client.postAsync<LoadPatientModel>(ServerOperations.Server.Myle_LoadPatient, {
                InactivableId: patientContactFacetId
            });
            patient = Patient.fromResponseData(response.data);
        }

        return patient;
    }
}

const PopulationBasedFundingPortfolioActions = new Actions(portfolioStore);

export { PopulationBasedFundingPortfolioActions };
